define([], () => {
    'use strict';

    /**
     * Creates a timer for an element defining a starting number of seconds
     * and optionally step value, and containing elements to represent time
     * parts
     *
     * Minute and second elements are required, hour and day elements are
     * supported but optional.
     */
    class MultiElementTimer {
        /**
         * @param {HTMLElement} container Container element for timer elements
         */
        constructor(container) {
            const startString = container.getAttribute('data-multi-element-timer');
            let start = parseInt(startString);
            if (isNaN(start)) {
                const dateString = document.querySelector(".event-time").textContent;
                start = Math.floor((new Date(dateString) - Date.now()) / 1000);
            }

            this.step = (container.hasAttribute('data-multi-element-timer-step')) ?
                parseInt(container.getAttribute('data-multi-element-timer-step')) :
                1;

            this.elements = {
                days: container.querySelector('[data-multi-element-timer-days]'),
                hours: container.querySelector('[data-multi-element-timer-hours]'),
                mins: container.querySelector('[data-multi-element-timer-mins]'),
                secs: container.querySelector('[data-multi-element-timer-secs]')
            };

            this.interval = null;

            this.startTimer(start);
        }

        /**
         * Formats a number or numeric string with a leading zero
         *
         * @param  {Number|String} time
         * @return {String}
         */
        static formatTime(time) {
            if ('string' !== typeof time) {
                time = time.toString();
            }

            return time;
        }

        /**
         * Gets stored value of a time element
         *
         * @param  {HTMLElement} element
         * @return {String}
         */
        static getValue(element) {
            if (element.nodeName === 'INPUT') {
                return element.value;
            }

            return element.textContent;
        }

        /**
         * Sets stored value of a time element
         *
         * @param  {HTMLElement} element
         * @param  {String} value
         * @return {void}
         */
        static setValue(element, value) {
            if (element.nodeName === 'INPUT') {
                element.value = value;
            }

            element.textContent = value;
        }

        /**
         * Converts a timestamp to total number of seconds
         *
         * @param  {String[]} parts A 2-4 member list of time parts
         * @return {Number}
         */
        static toSeconds(parts) {
            let seconds = parseInt(parts[parts.length - 1]) +
                parseInt(parts[parts.length - 2]) * 60;

            if (parts.length >= 3) {
                seconds += parseInt(parts[parts.length - 3]) * 3600;
            }

            if (parts.length === 4) {
                seconds += parseInt(parts[0]) * 86400;
            }

            return seconds;
        }

        /**
         * Converts a number of seconds into a timestamp
         *
         * @param  {Number} seconds
         * @return {String[]} A 2-4 member list of time parts
         */
        toTimestamp(seconds) {
            let parts = [];

            if (null !== this.elements.days) {
                const days = Math.floor(seconds / 86400);
                parts.push(MultiElementTimer.formatTime(days));
                seconds = seconds - days * 86400;
            }

            if (null !== this.elements.hours) {
                const hours = Math.floor(seconds / 3600);
                parts.push(MultiElementTimer.formatTime(hours));
                seconds = seconds - hours * 3600;
            }

            const minutes = Math.floor(seconds / 60);
            parts.push(MultiElementTimer.formatTime(minutes));
            seconds = seconds - minutes * 60;

            parts.push(MultiElementTimer.formatTime(seconds));

            return parts;
        }

        /**
         * Initiates the timer
         *
         * @param  {Number} start Number of seconds to start with
         * @return {void}
         */
        startTimer(start) {
            if ((this.step < 0) && (start <= 0)) {
                this.displayTimestamp(['0', '0', '0', '0']);
                return;
            }

            this.displayTimestamp(this.toTimestamp(start));

            this.interval = window.setInterval(
                this.updateTimer.bind(this),
                Math.abs(this.step * 1000)
            );
        }

        /**
         * Halts the timer
         *
         * @return {void}
         */
        stopTimer() {
            window.clearInterval(this.interval);
        }

        /**
         * Steps the timer value
         *
         * @return {void}
         */
        updateTimer() {
            let seconds = MultiElementTimer.toSeconds(this.getTimeStamp());
            seconds = seconds + this.step;

            if (this.step < 0 && seconds <= 0) {
                window.clearInterval(this.interval);
                this.displayTimestamp(['0', '0', '0', '0']);
                return;
            }

            this.displayTimestamp(this.toTimestamp(seconds));
        }

        /**
         * Populates a timestamp into the time elements
         *
         * @param  {String[]} parts A 2-4 member list of time parts
         * @return {void}
         */
        displayTimestamp(parts) {
            MultiElementTimer.setValue(this.elements.secs, parts[parts.length - 1]);
            MultiElementTimer.setValue(this.elements.mins, parts[parts.length - 2]);

            if (parts.length >= 3) {
                MultiElementTimer.setValue(this.elements.hours, parts[parts.length - 3]);
            }

            if (parts.length === 4) {
                MultiElementTimer.setValue(this.elements.days, parts[0]);
            }
        }

        /**
         * Gets a timestamp from the time elements
         *
         * @return {String[]} A 2-4 member list of time parts
         */
        getTimeStamp() {
            let parts = [];
            if (null !== this.elements.day) {
                parts.push(MultiElementTimer.getValue(this.elements.days));
            }

            if (null !== this.elements.hours) {
                parts.push(MultiElementTimer.getValue(this.elements.hours));
            }

            parts.push(MultiElementTimer.getValue(this.elements.mins));
            parts.push(MultiElementTimer.getValue(this.elements.secs));

            return parts;
        }
    }

    let timer = new MultiElementTimer( document.getElementsByClassName('countdown__container')[0]);
});